import { cloneDeep, range } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Message } from '@meitu/saas-ui';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { Layer } from '@/layers/layer';
import type { Cell, CellStyle, ITableLayer } from '@/version-types';
import { MAX_COL_COUNT, MAX_ROW_COUNT } from '../default-configs';
import { getTableEditState } from '../plugins/edit/state';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import { emptyBorder, getCellByIndexes, isEqualBorder } from '../utils/cell';
import { getReversedDirection } from '../utils/native';
import { getActiveTableLayer, getCellStyle } from '../utils/table';
import { applyTableLayout } from '../utils/table-layout';

type InsertDirection = 'topOrLeft' | 'bottomOrRight';

export const getRowOrColActions = (frameworkIdentity: string) => {
    const { initState } = getTableEditState(frameworkIdentity);
    const framework = getFramework(frameworkIdentity);

    /** 返回null, 则不需要改变 */
    const getBaseActionFn =
        <ActionType extends 'add' | 'del'>(actionType: ActionType, targetMode: 'row' | 'col') =>
        ({
            tableLayer,
            targetIndex,
            actionCount = 1,
            insertDirection: insertDirectionArg,
            onDeleteTableLayer,
        }: {
            tableLayer: ITableLayer;
            /** 若actionType='add', 则没有限制; 若actionType='del', 从targetIndex向右/下方删除actionCount个行/列 */
            targetIndex: number;
            /** 添加或删除几行/列, 默认为1 */
            actionCount?: number;
            /** 若actionType='add', 插入方向, 默认为向上(/向左)插入; 若actionType='del', 则没有这个参数 */
            insertDirection?: InsertDirection;
            onDeleteTableLayer?: () => void;
        }): ITableLayer | null => {
            if (actionCount <= 0) return null;

            const { $t } = getLocaleStore();

            initState();

            const { baseCells: originalBaseCells } = tableLayer.data;

            const newTableLayer = cloneDeep(tableLayer);
            const { allStyleId, cells: cellsProp, baseCells, styles } = newTableLayer.data;
            const cells = cellsProp.flat();
            const sideBarDirection = targetMode === 'row' ? 'vertical' : 'horizontal';

            const insertDirection: InsertDirection =
                targetIndex === baseCells[sideBarDirection].length
                    ? 'bottomOrRight'
                    : (insertDirectionArg ?? 'topOrLeft');

            const otherDirection = sideBarDirection === 'vertical' ? 'horizontal' : 'vertical';
            const MAX_COUNT = targetMode === 'row' ? MAX_ROW_COUNT : MAX_COL_COUNT;

            if (actionType === 'add') {
                const tip = () =>
                    Message.info(
                        $t((v, { r }) => r(v.tableLayer.hooks.maxSupportText, [MAX_ROW_COUNT, MAX_COL_COUNT])),
                    );
                if (baseCells[sideBarDirection].length >= MAX_COUNT) {
                    tip();
                    return null;
                }
                if (baseCells[sideBarDirection].length + actionCount > MAX_COUNT) {
                    tip();
                    actionCount = MAX_COUNT - baseCells[sideBarDirection].length;
                }

                /** 是否是最边缘的操作 */
                const isExtremeEdgeOperating =
                    insertDirection === 'bottomOrRight'
                        ? targetIndex === originalBaseCells[sideBarDirection].length
                        : targetIndex === 0;
                const extremeEdgeDir: 'top' | 'left' | 'bottom' | 'right' = (() => {
                    if (insertDirection === 'bottomOrRight') return targetMode === 'row' ? 'bottom' : 'right';
                    return targetMode === 'row' ? 'top' : 'left';
                })();
                const styleTemplateRowOrColIndex = insertDirection === 'topOrLeft' ? targetIndex : targetIndex - 1;
                type StyleTemplateCell = { cell: Cell; index: number };
                const styleTemplateCells: StyleTemplateCell[] = originalBaseCells[otherDirection].map((__, index) => {
                    const styleTemplateCell = getCellByIndexes({
                        tableLayerData: tableLayer.data,
                        indexes: {
                            rowIndex: targetMode === 'row' ? styleTemplateRowOrColIndex : index,
                            colIndex: targetMode === 'row' ? index : styleTemplateRowOrColIndex,
                        },
                    })!;
                    return {
                        cell: cloneDeep(styleTemplateCell),
                        index,
                    };
                });

                // 被targetIndex对应的线贯穿的cells
                const middleCells = cells.filter(
                    (cell) =>
                        cell &&
                        (targetMode === 'row' ? cell.vIndex : cell.hIndex) < targetIndex &&
                        (targetMode === 'row' ? cell.vIndex + cell.rowSpan : cell.hIndex + cell.colSpan) > targetIndex,
                ) as Cell[];

                // 1. update baseCells and cellsProp
                {
                    const newSize = baseCells[sideBarDirection][styleTemplateRowOrColIndex];
                    baseCells[sideBarDirection].splice(targetIndex, 0, ...Array(actionCount).fill(newSize));
                    // 先补占位的cells
                    if (targetMode === 'row') {
                        const getEmptyRowCells = () => Array(baseCells.horizontal.length).fill(null);
                        const newCols = range(actionCount).map(() => getEmptyRowCells());
                        cellsProp.splice(targetIndex, 0, ...newCols);
                    } else {
                        cellsProp.forEach((rowCells) => {
                            const newRowCells = Array(actionCount).fill(null);
                            rowCells.splice(targetIndex, 0, ...newRowCells);
                        });
                    }
                }

                // 2. update middleCells
                {
                    middleCells.forEach((cell) => {
                        targetMode === 'row' ? (cell.rowSpan += actionCount) : (cell.colSpan += actionCount);
                    });
                }

                // 3. update after cells
                {
                    const afterCells = cells.filter(
                        (cell) => cell && (targetMode === 'row' ? cell.vIndex : cell.hIndex) >= targetIndex,
                    ) as Cell[];
                    afterCells.forEach(
                        (cell) =>
                            cell &&
                            (targetMode === 'row' ? (cell.vIndex += actionCount) : (cell.hIndex += actionCount)),
                    );
                }

                // 4. insert cells
                {
                    baseCells[otherDirection].forEach((_, index) => {
                        // ignore middle cells
                        if (
                            middleCells?.some((cell) =>
                                targetMode === 'row'
                                    ? cell?.vIndex < targetIndex &&
                                      cell?.vIndex + cell?.rowSpan > targetIndex + 1 &&
                                      cell?.hIndex <= index &&
                                      cell?.hIndex + cell?.colSpan >= index + 1
                                    : cell?.hIndex < targetIndex &&
                                      cell?.hIndex + cell?.colSpan > targetIndex + 1 &&
                                      cell?.vIndex <= index &&
                                      cell?.vIndex + cell?.rowSpan >= index + 1,
                            )
                        ) {
                            return;
                        }
                        // 新插入的cell的样式, 用的是当前行、列最邻近的cell的样式
                        const styleIds = styleTemplateCells.find((item) => item.index === index)!.cell.styleIds;
                        for (let i = 0; i < actionCount; i++) {
                            const newCell: Cell = {
                                __renderId: uuidv4(),
                                vIndex: targetMode === 'row' ? targetIndex + i : index,
                                hIndex: targetMode === 'row' ? index : targetIndex + i,
                                colSpan: 1,
                                rowSpan: 1,
                                content: '',
                                styleIds: [...styleIds],
                            };
                            cellsProp[newCell.vIndex][newCell.hIndex] = newCell;
                        }
                    });
                }

                // 5. update border
                {
                    type CellStyleBorderProp = NonNullable<CellStyle['border']>;
                    const borderStylePool: { border: CellStyleBorderProp; styleId: string }[] = [];
                    Object.entries(styles).forEach(([styleId, style]) => {
                        const styleKeys = Object.keys(style);
                        if (styleKeys.length === 1 && styleKeys[0] === 'border') {
                            borderStylePool.push({
                                styleId,
                                border: style.border!,
                            });
                        }
                    });

                    if (isExtremeEdgeOperating) {
                        // 5-1. update border of otherSideCells
                        {
                            const otherSideCells = range(baseCells[otherDirection].length).map(
                                (index) =>
                                    getCellByIndexes({
                                        tableLayerData: newTableLayer.data,
                                        indexes: {
                                            colIndex: targetMode === 'row' ? index : styleTemplateRowOrColIndex,
                                            rowIndex: targetMode === 'row' ? styleTemplateRowOrColIndex : index,
                                        },
                                    })!,
                            );
                            otherSideCells.forEach((cell) => {
                                if (!cell) return;
                                const { border } = getCellStyle(cell, newTableLayer.data);
                                const effectedBorder = border?.[extremeEdgeDir];
                                const expectBorder = getTemplateBorder(
                                    targetMode === 'row' ? cell.hIndex : cell.vIndex,
                                )[getReversedDirection(extremeEdgeDir)];
                                if (isEqualBorder(effectedBorder, expectBorder)) return;

                                const newStyleBorder: CellStyleBorderProp = {
                                    top: emptyBorder,
                                    right: emptyBorder,
                                    bottom: emptyBorder,
                                    left: emptyBorder,
                                    ...border,
                                    [extremeEdgeDir]: expectBorder,
                                };
                                const newStyleId = appendNewBorderStyle(newStyleBorder);
                                if (cell.styleIds.includes(newStyleId)) {
                                    cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                                } else {
                                    cell.styleIds.push(newStyleId);
                                }
                            });
                        }

                        // 5-2. update border of insertedCells except for wrap border
                        {
                            const targetCells = (() => {
                                if (targetMode === 'row') {
                                    if (extremeEdgeDir === 'bottom') {
                                        return newTableLayer.data.cells.slice(targetIndex, -1).flat();
                                    }
                                    return newTableLayer.data.cells.slice(1, targetIndex).flat();
                                }
                                if (extremeEdgeDir === 'right') {
                                    return newTableLayer.data.cells
                                        .map((rowCells) => rowCells.slice(targetIndex, -1))
                                        .flat();
                                }
                                return newTableLayer.data.cells
                                    .map((rowCells) => rowCells.slice(1, targetIndex))
                                    .flat();
                            })() as Cell[];
                            targetCells.forEach((cell) => {
                                if (!cell) return;
                                const templateBorder = getTemplateBorder(
                                    targetMode === 'row' ? cell.hIndex : cell.vIndex,
                                );
                                const newStyleBorder: CellStyleBorderProp = {
                                    ...templateBorder,
                                    [extremeEdgeDir]: templateBorder[getReversedDirection(extremeEdgeDir)],
                                };
                                const newStyleId = appendNewBorderStyle(newStyleBorder);
                                if (cell.styleIds.includes(newStyleId)) {
                                    cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                                } else {
                                    cell.styleIds.push(newStyleId);
                                }
                            });
                        }

                        // 5-3. update wrap border
                        {
                            const targetCells = (() => {
                                if (targetMode === 'row') {
                                    if (extremeEdgeDir === 'bottom') {
                                        return newTableLayer.data.cells[newTableLayer.data.cells.length - 1];
                                    }
                                    return newTableLayer.data.cells[0];
                                }
                                if (extremeEdgeDir === 'right') {
                                    return newTableLayer.data.cells.map((rowCells) => rowCells[rowCells.length - 1]);
                                }
                                return newTableLayer.data.cells.map((rowCells) => rowCells[0]);
                            })() as Cell[];
                            targetCells.forEach((cell) => {
                                if (!cell) return;
                                const templateBorder = getTemplateBorder(
                                    targetMode === 'row' ? cell.hIndex : cell.vIndex,
                                );
                                const newStyleBorder = cloneDeep(templateBorder);
                                const newStyleId = appendNewBorderStyle(newStyleBorder);
                                if (cell.styleIds.includes(newStyleId)) {
                                    cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                                } else {
                                    cell.styleIds.push(newStyleId);
                                }
                            });
                        }
                    } else {
                        range(baseCells[otherDirection].length).forEach((index) => {
                            // ignore middle cells
                            if (
                                middleCells.some((cell) =>
                                    targetMode === 'row'
                                        ? cell?.vIndex < targetIndex &&
                                          cell?.vIndex + cell?.rowSpan > targetIndex + 1 &&
                                          cell?.hIndex <= index &&
                                          cell?.hIndex + cell?.colSpan >= index + 1
                                        : cell?.hIndex < targetIndex &&
                                          cell?.hIndex + cell?.colSpan > targetIndex + 1 &&
                                          cell?.vIndex <= index &&
                                          cell?.vIndex + cell?.rowSpan >= index + 1,
                                )
                            ) {
                                return;
                            }

                            const cell = getCellByIndexes({
                                tableLayerData: newTableLayer.data,
                                indexes: {
                                    colIndex: targetMode === 'row' ? index : targetIndex,
                                    rowIndex: targetMode === 'row' ? targetIndex : index,
                                },
                            })!;
                            const { border: templateCellStyleBorder } = [
                                allStyleId,
                                ...styleTemplateCells[index].cell.styleIds,
                            ].reduce<CellStyle>((res, styleId) => {
                                return {
                                    ...res,
                                    ...styles[styleId],
                                };
                            }, {});
                            const newStyleBorder = (() => {
                                if (targetMode === 'row') {
                                    const bilateralBorder =
                                        insertDirection === 'bottomOrRight'
                                            ? (templateCellStyleBorder?.bottom ?? emptyBorder)
                                            : (templateCellStyleBorder?.top ?? emptyBorder);
                                    const newStyleBorder: CellStyleBorderProp = {
                                        left: templateCellStyleBorder?.left ?? emptyBorder,
                                        right: templateCellStyleBorder?.right ?? emptyBorder,
                                        top: bilateralBorder,
                                        bottom: bilateralBorder,
                                    };
                                    return newStyleBorder;
                                }
                                const bilateralBorder =
                                    insertDirection === 'bottomOrRight'
                                        ? (templateCellStyleBorder?.right ?? emptyBorder)
                                        : (templateCellStyleBorder?.left ?? emptyBorder);
                                const newStyleBorder: CellStyleBorderProp = {
                                    top: templateCellStyleBorder?.top ?? emptyBorder,
                                    bottom: templateCellStyleBorder?.bottom ?? emptyBorder,
                                    left: bilateralBorder,
                                    right: bilateralBorder,
                                };
                                return newStyleBorder;
                            })();
                            const newStyleId = appendNewBorderStyle(newStyleBorder);
                            if (cell.styleIds.includes(newStyleId)) {
                                cell.styleIds = [...cell.styleIds.filter((id) => id !== newStyleId), newStyleId];
                            } else {
                                cell.styleIds.push(newStyleId);
                            }
                        });
                    }

                    function getTemplateBorder(
                        /** 如果插入的是行, 则index为vIndex; 列则反之 */
                        index: number,
                    ): CellStyleBorderProp {
                        // 新插入的cell的样式, 用的是当前行、列最邻近的cell的样式
                        const styleTemplateCell = styleTemplateCells.find((item) => item.index === index)!.cell;
                        const { border } = [allStyleId, ...styleTemplateCell.styleIds].reduce<CellStyle>(
                            (res, styleId) => {
                                return {
                                    ...res,
                                    ...styles[styleId],
                                };
                            },
                            {},
                        );
                        return {
                            top: emptyBorder,
                            right: emptyBorder,
                            bottom: emptyBorder,
                            left: emptyBorder,
                            ...border,
                        };
                    }
                    /** 返回styleId */
                    function appendNewBorderStyle(border: CellStyleBorderProp): string {
                        const isEqualBorderProp = (a: CellStyleBorderProp, b: CellStyleBorderProp) =>
                            isEqualBorder(a.top, b.top) &&
                            isEqualBorder(a.right, b.right) &&
                            isEqualBorder(a.bottom, b.bottom) &&
                            isEqualBorder(a.left, b.left);
                        const poolItem = borderStylePool.find((item) => isEqualBorderProp(item.border, border));
                        if (poolItem) return poolItem.styleId;

                        const newStyleId = (Math.max(...Object.keys(styles).map(Number)) + 1).toString();
                        styles[newStyleId] = { border };
                        borderStylePool.push({ border, styleId: newStyleId });
                        return newStyleId;
                    }
                }
            } else {
                // del

                // 删除整个表格
                if (actionCount === baseCells[sideBarDirection].length) {
                    onDeleteTableLayer?.();
                    return null;
                } else if (actionCount >= baseCells[sideBarDirection].length) {
                    // 不够删时就不删了: 比如表格就4行, 要删除大于4行就不允许删了
                    return null;
                }

                // 定义1 innerCell: 被删除区域完全包裹的cell
                // 定义2 middleSide1Cell: 被targetIndex对应的线贯穿的cell
                // 定义3 middleSide2Cell: 被targetIndex + actionCount对应的线贯穿、且没被targetIndex对应的线贯穿的cell
                // 普通情况: 是innerCell, 直接删除
                // 特殊情况1: 如果是middleSide1Cell, 直接修改colSpan/rowSpan
                // 特殊情况2: 如果是middleSide2Cell, 需要挪到targetIndex + actionCount对应的行/列, 并修改hIndex/vIndex和colSpan/rowSpan

                const middleSide1Cells = cells.filter(
                    (cell) =>
                        cell &&
                        (targetMode === 'row' ? cell.vIndex : cell.hIndex) < targetIndex &&
                        (targetMode === 'row' ? cell.vIndex + cell.rowSpan : cell.hIndex + cell.colSpan) > targetIndex,
                ) as Cell[];

                const middleSide2Cells = cells.filter(
                    (cell) =>
                        cell &&
                        (targetMode === 'row' ? cell.vIndex : cell.hIndex) >= targetIndex &&
                        (targetMode === 'row' ? cell.vIndex : cell.hIndex) < targetIndex + actionCount &&
                        (targetMode === 'row' ? cell.vIndex + cell.rowSpan : cell.hIndex + cell.colSpan) >
                            targetIndex + actionCount,
                ) as Cell[];

                const afterCells = cells.filter(
                    (cell) => cell && (targetMode === 'row' ? cell.vIndex : cell.hIndex) >= targetIndex + actionCount,
                ) as Cell[];

                // 1. update baseCells and cellsProp
                {
                    const tableEndIndex = baseCells[sideBarDirection].length - actionCount;
                    baseCells[sideBarDirection].splice(targetIndex, actionCount);
                    // 把位置发生变化的cells都从cellProp中去掉
                    if (targetMode === 'row') {
                        cellsProp.splice(targetIndex);
                        const getEmptyRowCells = () => Array(baseCells.horizontal.length).fill(null);
                        cellsProp.push(...range(tableEndIndex - targetIndex).map(() => getEmptyRowCells()));
                    } else {
                        cellsProp.forEach((rowCells) => {
                            rowCells.splice(targetIndex);
                            rowCells.push(...Array(tableEndIndex - targetIndex).fill(null));
                        });
                    }
                }

                // 2. update middleSide1Cells
                {
                    middleSide1Cells.forEach((c) => {
                        if (!c) return;
                        const cellEndIndex = targetMode === 'row' ? c.vIndex + c.rowSpan : c.hIndex + c.colSpan;
                        const reduceCount =
                            cellEndIndex > targetIndex + actionCount ? actionCount : cellEndIndex - targetIndex;
                        targetMode === 'row' ? (c.rowSpan -= reduceCount) : (c.colSpan -= reduceCount);
                    });
                }

                // 3. update middleSide2Cells
                {
                    middleSide2Cells.forEach((c) => {
                        if (!c) return;
                        const cellEndIndex = targetMode === 'row' ? c.vIndex + c.rowSpan : c.hIndex + c.colSpan;
                        if (targetMode === 'row') {
                            c.vIndex = targetIndex;
                            c.rowSpan = cellEndIndex - (targetIndex + actionCount);
                            cellsProp[targetIndex][c.hIndex] = c;
                        } else {
                            c.hIndex = targetIndex;
                            c.colSpan = cellEndIndex - (targetIndex + actionCount);
                            cellsProp[c.vIndex][targetIndex] = c;
                        }
                    });
                }

                // 4. update afterCells
                {
                    afterCells.forEach((c) => {
                        if (!c) return;
                        targetMode === 'row' ? (c.vIndex -= actionCount) : (c.hIndex -= actionCount);
                        cellsProp[c.vIndex][c.hIndex] = c;
                    });
                }
            }

            // finally. update table layout
            applyTableLayout(newTableLayer, { mode: 'repair' });

            getTableEditState(frameworkIdentity).setState({
                cellsSelectedInfo: null,
                rowOrColSelectedRectInfo: {
                    index:
                        actionType === 'add'
                            ? // 添加行/列时，应该选中新添加的第一行/列
                              targetIndex - 1 + actionCount
                            : // 删除行/列时，应该选中上一行/列
                              Math.max(targetIndex - 1, 0),
                    direction: targetMode === 'row' ? 'horizontal' : 'vertical',
                },
            });

            if (newTableLayer.data.baseCells.vertical.length !== newTableLayer.data.cells.length) {
                throw new Error('===设置行列数时, 数据对不上, 为了防止出现错误数据(可能导致页面崩溃), 直接中断');
            }

            return newTableLayer;
        };

    const addRow = getBaseActionFn('add', 'row');
    const addCol = getBaseActionFn('add', 'col');
    const delRow = getBaseActionFn('del', 'row');
    const delCol = getBaseActionFn('del', 'col');

    /** 针对cellsSelectedInfo, 向(上/左/下/右)(添加/删除)一(行/列) */
    function addOrDelRowOrColByCellsSelectedInfo(data: {
        actionType: 'add';
        targetMode: 'row';
        direction: 'top' | 'bottom';
        /** 添加几行/几列 */
        addCount: number;
    }): void;
    function addOrDelRowOrColByCellsSelectedInfo(data: {
        actionType: 'add';
        targetMode: 'col';
        direction: 'left' | 'right';
        /** 添加几行/几列 */
        addCount: number;
    }): void;
    function addOrDelRowOrColByCellsSelectedInfo(data: { actionType: 'del'; targetMode: 'row' | 'col' }): void;
    function addOrDelRowOrColByCellsSelectedInfo(data) {
        const { $t } = getLocaleStore();

        const { actionType, targetMode, direction, addCount } = data as {
            actionType: 'add' | 'del';
            targetMode: 'row' | 'col';
            direction: 'top' | 'left' | 'right' | 'bottom';
            addCount: number;
        };

        const tableLayer = getActiveTableLayer(frameworkIdentity);
        if (!tableLayer) return;
        const { horizontal: horizontalSizes, vertical: verticalSizes } = tableLayer.data.baseCells;
        const sideSizes = targetMode === 'row' ? verticalSizes : horizontalSizes;

        const { cellsSelectedInfo, rowOrColSelectedRectInfo } = getTableEditState(frameworkIdentity);
        const hasSelectedInfo = !!cellsSelectedInfo || !!rowOrColSelectedRectInfo;
        if (!hasSelectedInfo) return;

        const startHIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'vertical'
                ? rowOrColSelectedRectInfo.index
                : 0
            : cellsSelectedInfo!.rect.startHIndex;
        const startVIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'horizontal'
                ? rowOrColSelectedRectInfo.index
                : 0
            : cellsSelectedInfo!.rect.startVIndex;
        const endHIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'vertical'
                ? rowOrColSelectedRectInfo.index + 1
                : horizontalSizes.length
            : cellsSelectedInfo!.rect.endHIndex;
        const endVIndex = rowOrColSelectedRectInfo
            ? rowOrColSelectedRectInfo.direction === 'horizontal'
                ? rowOrColSelectedRectInfo.index + 1
                : verticalSizes.length
            : cellsSelectedInfo!.rect.endVIndex;

        if (actionType === 'add') {
            const addAction = targetMode === 'row' ? addRow : addCol;

            const targetIndex = (() => {
                switch (direction) {
                    case 'top':
                        return startVIndex;
                    case 'right':
                        return endHIndex;
                    case 'bottom':
                        return endVIndex;
                    case 'left':
                        return startHIndex;
                }
            })();

            if (targetIndex === -1) return;

            const indexDirection = ['top', 'left'].includes(direction) ? -1 : 1;
            const MAX_COUNT = ['top', 'bottom'].includes(direction) ? MAX_ROW_COUNT : MAX_COL_COUNT;
            const expectLatestIndex = targetIndex + indexDirection * addCount;
            if (sideSizes.length === MAX_COUNT || expectLatestIndex > MAX_COUNT) {
                Message.info($t((v, { r }) => r(v.tableLayer.hooks.maxSupportText, [MAX_ROW_COUNT, MAX_COL_COUNT])));
            }
            if (sideSizes.length === MAX_COUNT) {
                return;
            }
            let newTableLayer = addAction({
                tableLayer,
                targetIndex,
                insertDirection: ['top', 'left'].includes(direction) ? 'topOrLeft' : 'bottomOrRight',
                actionCount: addCount,
            });

            if (newTableLayer) {
                newTableLayer = addOrUpdateLayer({
                    frameworkIdentity,
                    layer: newTableLayer,
                    resetMaterialIds: true,
                });
                framework.service.frame.setActiveLayer(newTableLayer.id);
                framework.service.history.write(cloneDeep(tableLayer), newTableLayer);
            }
            return;
        }

        const delAction = targetMode === 'row' ? delRow : delCol;
        const targetIndex = targetMode === 'row' ? startVIndex : startHIndex;
        const actionCount = targetMode === 'row' ? endVIndex - startVIndex : endHIndex - startHIndex;

        let shouldDeleteAllTable = false;
        let newTableLayer = delAction({
            tableLayer,
            targetIndex,
            actionCount,
            onDeleteTableLayer: () => {
                shouldDeleteAllTable = true;
                new Layer(frameworkIdentity).delete(tableLayer.id);
            },
        });
        if (newTableLayer && !shouldDeleteAllTable) {
            newTableLayer = addOrUpdateLayer({
                frameworkIdentity,
                layer: newTableLayer,
                resetMaterialIds: true,
            });
            framework.service.frame.setActiveLayer(newTableLayer.id);
            framework.service.history.write(cloneDeep(tableLayer), newTableLayer);
        }
    }

    return {
        addRow,
        addCol,
        delRow,
        delCol,
        addOrDelRowOrColByCellsSelectedInfo,
    };
};
